import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  Typography, 
  useTheme, 
  Container,
  Button,
  useMediaQuery,
  Slide,
  Tooltip,
  Divider,
  Stack,
  useScrollTrigger
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon
} from '@mui/icons-material';
import { ColorModeContext } from '../App';
// Import pages
import Home from '../pages/Home';
import About from '../pages/About';
import Projects from '../pages/Projects';
import Skills from '../pages/Skills';
import Contact from '../pages/Contact';
import Footer from './Footer';
import { useRef } from 'react';

const drawerWidth = 200;

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  height: 70,
  display: 'flex',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
}));

const NavButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  color: theme.palette.text.secondary,
  position: 'relative',
  fontSize: '0.85rem',
  fontWeight: 500,
  letterSpacing: '0.02em',
  textTransform: 'capitalize',
  padding: theme.spacing(0.5, 1),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: 2,
    background: theme.palette.primary.main,
    opacity: 0,
    transform: 'scaleX(0)',
    transformOrigin: 'center',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    '&::after': {
      opacity: 0.7,
      transform: 'scaleX(0.5)',
    },
  },
  '&.active': {
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&::after': {
      opacity: 1,
      transform: 'scaleX(0.5)',
    },
  },
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(1),
  fontSize: '1rem',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: theme.palette.secondary.main,
    transform: 'translateY(-2px)',
    backgroundColor: 'transparent',
  },
}));

const ScrollTopButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  width: 32,
  height: 32,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.secondary.main,
  },
  zIndex: 1000,
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    backgroundColor: theme.palette.background.default,
    borderLeft: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 0),
  }
}));

const MobileNavItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  },
  '&.active': {
    backgroundColor: 'transparent',
    color: theme.palette.secondary.main,
  },
}));

const MobileNavText = styled(ListItemText)(({ theme, active }) => ({
  '& .MuiTypography-root': {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontSize: '0.95rem',
    letterSpacing: '0.01em',
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
    fontWeight: active ? 600 : 500,
    textTransform: 'none',
  },
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  letterSpacing: '0.02em',
  fontSize: '1.1rem',
  color: theme.palette.text.primary,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -14,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
}));

const ResumeButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  padding: theme.spacing(0.8, 2),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 500,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const ThemeToggleButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  borderRadius: '50%',
  marginLeft: theme.spacing(1),
  padding: theme.spacing(1),
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.03)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(0, 0, 0, 0.06)',
    transform: 'translateY(-2px)',
  },
}));

function ScrollTop(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Slide direction="up" in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 2 }}
      >
        {children}
      </Box>
    </Slide>
  );
}

const Layout = () => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  
  const homeRef = useRef(null);
  const aboutRef = useRef(null);
  const projectsRef = useRef(null);
  const skillsRef = useRef(null);
  const contactRef = useRef(null);

  const scrollToSection = (ref, section) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
    setActiveSection(section);
    setOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      
      const isInView = (ref) => {
        if (!ref.current) return false;
        const element = ref.current;
        const elementTop = element.offsetTop;
        const elementBottom = elementTop + element.offsetHeight;
        return scrollPosition >= elementTop && scrollPosition < elementBottom;
      };
      
      if (isInView(homeRef)) setActiveSection('home');
      else if (isInView(aboutRef)) setActiveSection('about');
      else if (isInView(projectsRef)) setActiveSection('projects');
      else if (isInView(skillsRef)) setActiveSection('skills');
      else if (isInView(contactRef)) setActiveSection('contact');
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { text: 'Home', id: 'home', ref: homeRef },
    { text: 'About', id: 'about', ref: aboutRef },
    { text: 'Projects', id: 'projects', ref: projectsRef },
    { text: 'Skills', id: 'skills', ref: skillsRef },
    { text: 'Contact', id: 'contact', ref: contactRef },
  ];

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <StyledAppBar position="fixed">
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: '100%', minHeight: '60px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Logo><span style={{color:theme.palette.secondary.main}}>/</span>KumarAv1nash</Logo>
            </Box>
            
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NavButton
                  className={activeSection === 'home' ? 'active' : ''}
                  onClick={() => scrollToSection(homeRef, 'home')}
                >
                  Home
                </NavButton>
                <NavButton
                  className={activeSection === 'about' ? 'active' : ''}
                  onClick={() => scrollToSection(aboutRef, 'about')}
                >
                  About
                </NavButton>
                <NavButton
                  className={activeSection === 'projects' ? 'active' : ''}
                  onClick={() => scrollToSection(projectsRef, 'projects')}
                >
                  Projects
                </NavButton>
                <NavButton
                  className={activeSection === 'skills' ? 'active' : ''}
                  onClick={() => scrollToSection(skillsRef, 'skills')}
                >
                  Skills
                </NavButton>
                <NavButton
                  className={activeSection === 'contact' ? 'active' : ''}
                  onClick={() => scrollToSection(contactRef, 'contact')}
                >
                  Contact
                </NavButton>
                <Tooltip title={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                  <ThemeToggleButton onClick={colorMode.toggleColorMode} aria-label="toggle dark/light mode">
                    {theme.palette.mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                  </ThemeToggleButton>
                </Tooltip>
              </Box>
            )}
            
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                  <ThemeToggleButton onClick={colorMode.toggleColorMode} aria-label="toggle dark/light mode">
                    {theme.palette.mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                  </ThemeToggleButton>
                </Tooltip>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleDrawerOpen}
                  sx={{ display: { sm: 'block', md: 'none' }, ml: 1 }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </StyledAppBar>
      
      <MobileDrawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        variant="temporary"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, alignItems: 'center' }}>
          <Logo>
            Avinash Kumar
          </Logo>
          <IconButton onClick={handleDrawerClose} sx={{ color: 'text.secondary', padding: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ my: 1, borderColor: 'divider' }} />
        <List sx={{ mb: 'auto' }}>
          {menuItems.map((item, index) => (
            <MobileNavItem
              button
              key={item.text}
              onClick={() => scrollToSection(item.ref, item.id)}
              className={activeSection === item.id ? 'active' : ''}
            >
              <MobileNavText 
                primary={`${item.text}`}
                active={activeSection === item.id}
              />
            </MobileNavItem>
          ))}
        </List>
        <Divider sx={{ my: 1, borderColor: 'divider' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Box sx={{ display: 'flex' }}>
            <Tooltip title="GitHub">
              <SocialIcon size="small" href="https://github.com/kumarav1nash" target="_blank">
                <GitHubIcon sx={{ fontSize: '1rem' }} />
              </SocialIcon>
            </Tooltip>
            <Tooltip title="LinkedIn">
              <SocialIcon size="small" href="https://www.linkedin.com/in/kumarav1nash/" target="_blank">
                <LinkedInIcon sx={{ fontSize: '1rem' }} />
              </SocialIcon>
            </Tooltip>
          </Box>
          <Box>
            <Tooltip title={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              <ThemeToggleButton 
                onClick={colorMode.toggleColorMode} 
                aria-label="toggle dark/light mode"
                size="small"
              >
                {theme.palette.mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </ThemeToggleButton>
            </Tooltip>
          </Box>
        </Box>
      </MobileDrawer>
      
      <Main>
        <Toolbar sx={{ minHeight: '60px !important' }} />
        <Box ref={homeRef} id="home">
          <Home />
        </Box>
        <Box ref={aboutRef} id="about">
          <About />
        </Box>
        <Box ref={projectsRef} id="projects">
          <Projects />
        </Box>
        <Box ref={skillsRef} id="skills">
          <Skills />
        </Box>
        <Box ref={contactRef} id="contact">
          <Contact />
        </Box>
        
        <Footer />
      </Main>
      
      <ScrollTop>
        <ScrollTopButton size="small">
          <KeyboardArrowUpIcon fontSize="small" />
        </ScrollTopButton>
      </ScrollTop>
    </Box>
  );
};

export default Layout;